import React, { useReducer, useCallback } from "react";
import {
  Typography,
  List,
  Box,
  ListItem,
  Accordion,
  makeStyles,
  AccordionDetails,
  AccordionSummary,
  Link as MuiLink,
  ListItemText,
  Radio,
  styled,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Link from "next/link";
import dynamic from "next/dynamic";

const Lottie = dynamic(() => import("react-lottie"), { ssr: false });

import { formatPriceCents, isProductNew, newState } from "./utils";
import animatedCircle from "../funnel/assets/animatedCircle.json";
import ProductDelivery from "./ProductDelivery";
import { getAvailableProductsFromList } from "../localOfferEligibility/LocalOfferEligibility";
import { commercialCategoriesStaticData } from "data/categories";

const circleAnimationOptions = {
  loop: false,
  autoplay: false,
  animationData: animatedCircle,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const EnergyBlogLink = styled("a")(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: "underline",
}));

const formatFiche = (dict) => {
  const keys = Object.keys(dict).filter((key) => dict[key] && dict[key] !== "" && typeof dict[key] !== "object");
  return (
    <Box pl="20px" clone>
      <ul>
        {keys.map((key) => (
          <li key={key}>
            <>
              <Box fontWeight={600} mr={1} component="span">
                {`${key === "Classe énergétique" ? "Ancienne classe énergétique" : key} :`}
              </Box>
              {key.toLocaleLowerCase().includes("classe énergétique") ? (
                <EnergyBlogLink
                  target="_blank"
                  href="https://murfy.fr/blog/nouvelles-etiquettes-energie/"
                  rel="noopener noreferrer"
                >
                  {dict[key]}
                </EnergyBlogLink>
              ) : (
                dict[key]
              )}
            </>
          </li>
        ))}
      </ul>
    </Box>
  );
};

const ProductDescription = ({ appearanceSlug = "", productDescription = "", productBarcode = "" }) => {
  const classes = useStyle();

  const desc = commercialCategoriesStaticData.find((a) => {
    return a.id === appearanceSlug;
  });

  if (desc) {
    if (productDescription !== "") {
      return (
        <>
          {desc.description}
          <br />
          <br />
          <pre className={classes.pre}>{productDescription}</pre>
          <br />
          <br />
          <span>REF : {productBarcode.toUpperCase()}</span>
        </>
      );
    }
    return (
      <>
        {desc.description}
        <br />
        <br />
        <span>REF : {productBarcode.toUpperCase()}</span>
      </>
    );
  }

  return <pre className={classes.pre}>{productDescription}</pre>;
};

function ProductInformationBloc({ product, category, offline }) {
  const classes = useStyle();
  const [expanded, setExpanded] = useReducer((prevState, action) => {
    if (action === prevState) {
      return null;
    }
    return action;
  }, 1);

  const getDimensions = useCallback(() => {
    return `${product?.functional_characteristics.Dimensions.L}x${product?.functional_characteristics.Dimensions.H}x${product?.functional_characteristics.Dimensions.P}cm`;
  }, [product]);

  return (
    <div className={classes.container}>
      {offline ? (
        <Typography color="textPrimary" className={classes.bold} variant="h2">
          {product.model_reference} - {product.appearance}
        </Typography>
      ) : (
        !isProductNew(product) && (
          <>
            <Typography color="textPrimary" className={classes.bold} variant="h2">
              Apparence de l&rsquo;appareil
            </Typography>
            <List>
              {commercialCategoriesStaticData
                .filter((cat) => cat.id !== newState)
                .map((appearance) => {
                  let appearanceProduct = getAvailableProductsFromList(product.all_appearances)
                    .sort((prev, next) => (prev.selling_price_withtax > next.selling_price_withtax ? 1 : -1))
                    .find((a) => a.appearance_trimmed === appearance.id);

                  const selected = product.appearance_slug === appearanceProduct?.appearance_trimmed;
                  return (
                    <ListItem
                      button
                      key={appearance.id}
                      className={classes.listItem}
                      selected={selected}
                      disabled={!appearanceProduct}
                    >
                      <Radio
                        disableRipple
                        checked={selected}
                        color="primary"
                        className={`${classes.radio} ${appearanceProduct ? "" : classes.disabledRadio}`}
                      />
                      <ListItemText
                        primary={
                          appearanceProduct ? (
                            <Link
                              href="/reconditionne/[category]/[category1]/[product]"
                              scroll={false}
                              as={`/reconditionne/${category.slug}/${
                                product.categories.length ? product.categories[0] : "simple"
                              }/${appearanceProduct.slug}`}
                              passHref
                            >
                              <MuiLink className={classes.link} variant="body1">
                                {appearance.name}
                              </MuiLink>
                            </Link>
                          ) : (
                            appearance.name
                          )
                        }
                      />
                      <Box ml={1}>
                        {appearanceProduct && (
                          <div className={classes.priceContainer}>
                            <Typography className={classes.price} component="span" variant="body2">
                              {formatPriceCents(appearanceProduct.selling_price_withtax)}
                            </Typography>
                            <div className={classes.animationContainer}>
                              <Lottie
                                options={circleAnimationOptions}
                                height={170}
                                isClickToPauseDisabled
                                width={170}
                                isStopped={!selected}
                                Animated={selected}
                              />
                            </div>
                          </div>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
            </List>
            <ProductDelivery />
          </>
        )
      )}
      <div className={classes.accordionContainer}>
        <Accordion className={classes.firstAccordion} onChange={() => setExpanded(1)} expanded={expanded === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography variant="h2" className={classes.bold}>
              Commentaire de nos réparateurs
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography className={classes.description}>
              <ProductDescription
                appearanceSlug={product.appearance_slug}
                productDescription={product?.appearance_comments}
                productBarcode={product.barcode}
              />
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className={classes.accordionContainer}>
        <Accordion onChange={() => setExpanded(3)} expanded={expanded === 3} className={classes.accordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography variant="h2" className={classes.bold}>
              Dimensions
            </Typography>
          </AccordionSummary>
          {product.functional_characteristics?.Dimensions && (
            <AccordionDetails>
              <Box display="flex" clone>
                <Typography variant="body1" component="div">
                  <Box fontWeight={600} mr={1} display="flex" alignItems="center">
                    <span>L</span>
                    <Box component="span" mt="-0.1em" mx="2px">
                      x
                    </Box>
                    <span>H</span>
                    <Box component="span" mt="-0.1em" mx="2px">
                      x
                    </Box>
                    <span>P</span>
                  </Box>
                  {getDimensions()}
                </Typography>
              </Box>
            </AccordionDetails>
          )}
        </Accordion>
      </div>

      {product.functional_characteristics && (
        <div className={classes.accordionContainer}>
          <Accordion onChange={() => setExpanded(4)} expanded={expanded === 4} className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography variant="h2" className={classes.bold}>
                Fiche technique
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography component="div">{formatFiche(product.functional_characteristics)}</Typography>
            </AccordionDetails>
          </Accordion>
        </div>
      )}
    </div>
  );
}

const useStyle = makeStyles((theme) => ({
  price: {
    fontWeight: 700,
  },
  pre: {
    fontFamily: "inherit",
    whiteSpace: "break-spaces",
  },
  radio: {
    marginRight: theme.spacing(),
    "&.MuiRadio-root": {
      color: theme.palette.primary.main,
    },
  },
  disabledRadio: {
    "&.MuiRadio-root": {
      color: "transparent",
    },
  },
  container: {
    backgroundColor: "white",
    padding: `${theme.spacing(2)}px 0`,
    [theme.breakpoints.down("md")]: {
      padding: 0,
      overflowX: "hidden",
    },
  },
  description: {
    color: "#787878",
    fontSize: 16,
    "& span": {
      fontSize: 13,
    },
  },
  priceContainer: {
    position: "relative",
    marginLeft: "auto",
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(2),
    },
  },
  animationContainer: {
    position: "absolute",
    left: -56,
    top: -76,
  },
  accordion: {
    boxShadow: "none",
  },
  accordionContainer: {
    "& .MuiPaper-root": {
      borderBottom: "1px solid #DBDFE8",
      borderRadius: 0,
      "& .MuiAccordionSummary-root": {
        padding: 0,
      },
    },
  },
  bold: {
    fontWeight: 500,
    fontSize: 19,
  },
  listItem: {
    borderRadius: 14,
    position: "relative",
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
    paddingLeft: theme.spacing(),
    borderStyle: "solid",
    "&.MuiListItem-button": {
      backgroundColor: theme.palette.containerGrey,
    },
    "&.Mui-disabled": {
      opacity: 0.5,
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.containerGrey,
    },
  },
  firstAccordion: {
    boxShadow: "none",
    "&::before": {
      opacity: 0,
    },
  },
  link: {
    color: "inherit",
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      // Just in case `pointer-events: none` is set on a parent
      pointerEvents: "auto",
      content: "''",
      // IE10 bugfix, see https://stackoverflow.com/questions/16947967/ie10-hover-pseudo-class-doesnt-work-without-background-color
      backgroundColor: "rgba(0, 0, 0, 0)",
    },
  },
}));

export default ProductInformationBloc;
